import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import type {
  CustomDataUploadResult,
  CustomOrder,
  CustomOrderListParams,
  CustomOrderListResult,
  CustomOrderSubmitPayload,
  CustomOrderSubmitResult,
  CustomOrderUpdatePayload,
  AssignableUser,
} from "@/lib/types/custom-data.types";

/**
 * Custom Data (custom order) API, against our Express proxy at
 * /api/custom-data (packages/server/api/custom-data):
 *  - /upload, /download: files in the private Spaces bucket (config/s3.ts)
 *  - /orders, /orders/:id: Infynd-CL-Custom-Order-Service, envelope
 *    `{ success, message?, data }` relayed as is.
 */

interface Envelope<T> {
  success?: boolean;
  message?: string;
  data: T;
}

export const customDataApi = createApi({
  reducerPath: "customDataApi",
  baseQuery: axiosBaseQuery("/api/custom-data"),
  tagTypes: ["CustomOrders", "CustomOrder", "ManagedOrders", "ManagedOrder"],
  endpoints: (builder) => ({
    /** Uploads one reference file (multer field `file`, 25 MB max server-side). */
    uploadCustomDataFile: builder.mutation<CustomDataUploadResult, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return { url: "/upload", method: "POST", body: formData };
      },
      transformResponse: (response: Envelope<CustomDataUploadResult>) => response.data,
    }),

    /** Resolves a stored `fileUrl` (reference or delivery file) to a fresh, short-lived signed download link. */
    getCustomOrderDownloadLink: builder.query<{ downloadUrl: string }, { url: string; fileName?: string }>({
      query: ({ url, fileName }) => ({ url: "/download", params: { url, ...(fileName ? { fileName } : {}) } }),
      transformResponse: (response: Envelope<{ downloadUrl: string }>) => response.data,
    }),

    /** Logs the customer's download of a delivery file (`fileUrl` as returned on the order); the data team is alerted on its first. */
    recordCustomOrderDownload: builder.mutation<void, { id: string; fileUrl: string }>({
      query: ({ id, fileUrl }) => ({
        url: `/orders/${encodeURIComponent(id)}/downloads`,
        method: "POST",
        body: { fileUrl },
      }),
    }),

    submitCustomOrder: builder.mutation<CustomOrderSubmitResult, CustomOrderSubmitPayload>({
      query: (body) => ({ url: "/orders", method: "POST", body }),
      transformResponse: (response: Envelope<CustomOrderSubmitResult>) => response.data,
      invalidatesTags: ["CustomOrders"],
    }),

    /** Whether the user is InFynd staff by email domain: if so, full orders need an SO number. */
    getInternalUser: builder.query<{ isInternal: boolean }, void>({
      query: () => ({ url: "/internal-user" }),
      transformResponse: (response: Envelope<{ isInternal?: boolean }>) => ({
        isInternal: response.data?.isInternal === true,
      }),
    }),

    getCustomOrders: builder.query<CustomOrderListResult, CustomOrderListParams>({
      query: (params) => ({ url: "/orders", params }),
      transformResponse: (response: Envelope<CustomOrderListResult>) => response.data,
      providesTags: ["CustomOrders"],
    }),

    /* Data team only — the /manage routes 404 for anyone without users.customOrderRole. */

    getManagedOrders: builder.query<CustomOrderListResult, CustomOrderListParams>({
      query: (params) => ({ url: "/manage/orders", params }),
      transformResponse: (response: Envelope<CustomOrderListResult>) => response.data,
      providesTags: ["ManagedOrders"],
    }),

    getManagedOrder: builder.query<CustomOrder, string>({
      query: (id) => ({ url: `/manage/orders/${encodeURIComponent(id)}` }),
      transformResponse: (response: Envelope<CustomOrder>) => response.data,
      providesTags: (_r, _e, id) => [{ type: "ManagedOrder", id }],
    }),

    /**
     * Staff an order can be assigned to. The Express route isn't wired yet —
     * it waits on the Custom Order service's endpoint — so this 404s and the
     * picker shows "not available yet" until then.
     */
    getAssignableUsers: builder.query<AssignableUser[], void>({
      query: () => ({ url: "/manage/assignable-users" }),
      transformResponse: (response: Envelope<AssignableUser[]>) => response.data ?? [],
    }),

    updateCustomOrder: builder.mutation<CustomOrder, { id: string; body: CustomOrderUpdatePayload }>({
      query: ({ id, body }) => ({ url: `/manage/orders/${encodeURIComponent(id)}`, method: "PATCH", body }),
      transformResponse: (response: Envelope<CustomOrder>) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        "ManagedOrders",
        "CustomOrders",
        { type: "ManagedOrder", id },
        { type: "CustomOrder", id },
      ],
    }),

    /** By Mongo `_id` — the service 404s on the readable orderId. */
    getCustomOrder: builder.query<CustomOrder, string>({
      query: (id) => ({ url: `/orders/${encodeURIComponent(id)}` }),
      transformResponse: (response: Envelope<CustomOrder>) => response.data,
      providesTags: (_r, _e, id) => [{ type: "CustomOrder", id }],
    }),
  }),
});

export const {
  useUploadCustomDataFileMutation,
  useLazyGetCustomOrderDownloadLinkQuery,
  useRecordCustomOrderDownloadMutation,
  useSubmitCustomOrderMutation,
  useGetInternalUserQuery,
  useGetCustomOrdersQuery,
  useGetCustomOrderQuery,
  useUpdateCustomOrderMutation,
  useGetManagedOrdersQuery,
  useGetManagedOrderQuery,
  useGetAssignableUsersQuery,
} = customDataApi;
